import React from 'react';

import { useTranslation } from 'react-i18next';
import { LoadingCardBody } from '@flexible-reports/components/loading-card-body';
import { Form, FormRow } from '@amzn/storm-ui-column-form-kit';
import { Card, CardHeader, Flex, Heading, InlineAlert, Input } from '@amzn/storm-ui';
import styled from 'styled-components';
import { isCaracalReportHmhEnabled } from '@flexible-reports/utils/app-utils';
import { HMHAnchor } from '@amzn/help-me-here-client';
import { STATIC_CREATE_A_REPORT_GUID } from '@flexible-reports/pages/landing/welcome-info/constants';

export const DEFAULT_CHARACTER_LIMIT = 256;

const FormRowNoMargin = styled(FormRow)`
  margin-block-end: ${({ theme }) => theme.spacing.none.default};
`;

export const StyledCardHeader = styled(CardHeader)`
  & > div {
    width: 100%;
  }
`;
type ReportNameInputProps = {
  reportName?: string;
  reportNameErrors?: string[];
  onNameChange: (reportName: string) => void;
  isLoading?: boolean;
  characterLimit?: number;
};

const ReportNameInput = ({
  reportName,
  reportNameErrors = [],
  onNameChange,
  isLoading = false,
  characterLimit = DEFAULT_CHARACTER_LIMIT
}: ReportNameInputProps) => {
  const { t: translate } = useTranslation();

  const croppedReportName = (reportName ?? '').slice(0, characterLimit);
  const remainingCharacters = characterLimit - croppedReportName.length;

  return (
    <Card taktId='report-settings-card'>
      {isCaracalReportHmhEnabled() && (
        <StyledCardHeader>
          <Flex direction='row' justifyContent='space-between' alignItems='center' columnGap='base'>
            <Heading renderAs='h4'>{translate('sspa_rcm_ad_reports_card_header_report_name_header', 'Report name')}</Heading>
            <HMHAnchor
              key={STATIC_CREATE_A_REPORT_GUID.contentId}
              contentId={STATIC_CREATE_A_REPORT_GUID.contentId}
              pageId={STATIC_CREATE_A_REPORT_GUID.pageId}
            />
          </Flex>
        </StyledCardHeader>
      )}
      {isLoading ? (
        <LoadingCardBody />
      ) : (
        <Form id='report-name-form'>
          <FormRowNoMargin
            label={translate('sspa_rcm_ad_reports_card_header_report_name', 'Report name')}
            id='report-name'
            controlComponents={[
              id => (
                <>
                  <Input
                    id={id}
                    value={croppedReportName}
                    maxLength={characterLimit}
                    onChange={({ target }) => onNameChange(target.value)}
                    message={() => (
                      <>
                        {reportNameErrors.map((error: string, index: number) => (
                          <InlineAlert key={index} message={error} variant='error' />
                        ))}
                      </>
                    )}
                    statusType={reportNameErrors.length > 0 ? 'error' : undefined}
                  />
                  <InlineAlert
                    message={translate('flexible_reports_name_characters_remaining', {
                      count: remainingCharacters,
                      defaultValue_one: '{{count}} character remaining',
                      defaultValue_other: '{{count}} characters remaining'
                    })}
                  />
                </>
              )
            ]}
          />
        </Form>
      )}
    </Card>
  );
};

export { ReportNameInput };
